/**
 * Top-of-tree effect: write a self-referential canonical for EVERY route on
 * mount and on route change. Fixes the sitewide "homepage canonical" audit
 * finding where pages that didn't call `usePageSeo` inherited the canonical
 * baked into public/index.html.
 *
 * Rules:
 * - Always set <link rel="canonical" href="PROD_ORIGIN + location.pathname">.
 * - Pages that need a different canonical (e.g. preferred-URL consolidation)
 *   should pass `canonical` to usePageSeo and that will overwrite this.
 * - Query strings and hash are intentionally dropped from the canonical.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const PROD_ORIGIN = "https://websitetech.pro";

export default function SelfCanonical() {
  const location = useLocation();
  useEffect(() => {
    const href = `${PROD_ORIGIN}${location.pathname}`;
    // Collapse any duplicate canonical tags that CRA's index.html might have.
    const existing = document.head.querySelectorAll('link[rel="canonical"]');
    existing.forEach((n, i) => { if (i > 0) n.remove(); });
    let el = document.head.querySelector('link[rel="canonical"]');
    if (!el) {
      el = document.createElement("link");
      el.rel = "canonical";
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);

    // Keep OG url synced to the same path for consistency with social crawlers.
    let og = document.head.querySelector('meta[property="og:url"]');
    if (!og) {
      og = document.createElement("meta");
      og.setAttribute("property", "og:url");
      document.head.appendChild(og);
    }
    og.setAttribute("content", href);

    // Default hreflang: en + x-default both point at the current URL. Pages that
    // ship a dedicated translated variant can override this via useHreflangAlternates.
    const existingHreflang = document.head.querySelectorAll('link[data-hreflang-default="1"]');
    existingHreflang.forEach((n) => n.remove());
    const managed = document.head.querySelectorAll('link[data-hreflang-managed="1"]');
    if (managed.length === 0) {
      const en = document.createElement("link");
      en.setAttribute("rel", "alternate");
      en.setAttribute("hreflang", "en");
      en.setAttribute("href", href);
      en.setAttribute("data-hreflang-default", "1");
      document.head.appendChild(en);
      const xd = document.createElement("link");
      xd.setAttribute("rel", "alternate");
      xd.setAttribute("hreflang", "x-default");
      xd.setAttribute("href", href);
      xd.setAttribute("data-hreflang-default", "1");
      document.head.appendChild(xd);
    }
  }, [location.pathname]);
  return null;
}
